import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { Button, Typography } from "@material-ui/core";

import UsersStore from "stores/UsersStore";

import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import Page from "components/Layout/Page";
import SimpleField from "components/SimpleField";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import LoadingButton from "components/LoadingButton";
import FormControl, { TextFieldControl } from "components/FormControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import ConfirmationDialog from "components/ConfirmationDialog";
import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { compose, emptyValidator, emailValidator } from "utils/validators";

import {
  ButtonContainer,
  FormContainer,
  Row,
  SearchFieldContainer,
  UserInfo,
  UserInfoField,
} from "./styles";

type Props = {} & RouteComponentProps & GlobalPopupProps & SnackbarContextProps;

@observer
class Users extends React.Component<Props> {
  @observable email = "";
  @observable isSearched = false;

  @computed get user() {
    return UsersStore.userFoundedByEmail;
  }

  @computed get resetPasswordText() {
    return this.user
      ? `Are you sure you want to reset the password for ${this.user.name} to the default one?`
      : ``;
  }

  componentDidMount() {
    UsersStore.reset();
  }

  onInputChange = (value: string) => {
    this.email = value;
    this.isSearched = false;
  };

  onSubmit = async () => {
    await UsersStore.search(this.email);
    this.isSearched = true;
  };

  onReset = (close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      if (this.user) {
        await UsersStore.resetPassword(this.user.email);
      }

      close();
      setMessage("Password reset successfully", SNACKBAR_TYPES.SUCCESS);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onResetPasswordClick = () => {
    const { openPopup } = this.props;

    openPopup({
      key: "ShowScheduleInfo",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title="Reset Password"
          text={this.resetPasswordText}
          declineText="Cancel"
          acceptText="Confirm"
          onDecline={close}
          onAccept={this.onReset(close)}
          isLoading={UsersStore.isManaging}
        />
      ),
    });
  };

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={UsersStore.isSearching || UsersStore.isManaging}
      fullWidth
    >
      {"Search"}
    </LoadingButton>
  );

  render() {
    return (
      <Container header={<Header />}>
        <Page title="Users">
          <FormControl onSubmit={this.onSubmit}>
            <FormContainer>
              <Row>
                <SearchFieldContainer>
                  <TextFieldControl
                    onInputChange={this.onInputChange}
                    variant="outlined"
                    margin="none"
                    name="email"
                    label="Email"
                    placeholder="Specify an email address"
                    validate={compose(emptyValidator, emailValidator)}
                    fullWidth
                    required
                  />
                </SearchFieldContainer>
                <ButtonContainer>
                  <SubmitButtonControl render={this.renderButton} />
                </ButtonContainer>
              </Row>
              <Row>
                {this.user ? (
                  <UserInfo>
                    <UserInfoField>
                      <SimpleField label="Name" value={this.user.name} />
                    </UserInfoField>
                    <UserInfoField>
                      <SimpleField label="Email" value={this.user.email} />
                    </UserInfoField>
                    <UserInfoField>
                      <SimpleField
                        label="Subscription status"
                        value={
                          this.user.isSubscriptionActive ? "Active" : "Inactive"
                        }
                      />
                    </UserInfoField>

                    <ButtonContainer>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={this.onResetPasswordClick}
                      >
                        {"Reset Password"}
                      </Button>
                    </ButtonContainer>
                  </UserInfo>
                ) : this.isSearched ? (
                  <Typography>{"User not found"}</Typography>
                ) : null}
              </Row>
            </FormContainer>
          </FormControl>
        </Page>
      </Container>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Users)));
